import * as React from "react";
import repository from "client/repository";
import {
    DataTable,
    DataTableBody,
    DataTableRow,
    DataTableRowColumn,
    DataTableRowHeaderColumn
} from "components/DataTable";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import PaperLayout from "components/PaperLayout";
import {default as OverflowMenu} from "components/Menu/OverflowMenu";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import ActionList from "components/ActionList/ActionList";
import {InternalRedirect} from "components/Navigation/InternalRedirect";
import {AzureRegionResource} from "client/resources/azureRegionResource";
import {azureRegionsRouteLinks} from "./azureRegionsRouteLinks";
import ExternalLink from "components/Navigation/ExternalLink";
import externalSystemLinks from "externalSystemLinks";
import SimpleDataTable from "components/SimpleDataTable/SimpleDataTable";
import {RegionPublicIpPrefixResource} from "client/resources/regionPublicIpPrefixResource";
import {MenuItem} from "components/Menu/OverflowMenu";
import AddRegionPublicIpPrefixDialog from "./AddRegionPublicIpPrefixDialog";
import ChangeRegionPublicIpPrefixDialog, {changes} from "./ChangeRegionPublicIpPrefixDialog";
import DeleteRegionPublicIpPrefixDialog from "./DeleteRegionPublicIpPrefixDialog";
import {canDelete, getAllowedChanges} from "./regionPublicIpPrefixChanges";
import {AdministratorRoleCallout} from "shared/AdministratorRoleCallout";

class PublicIpPrefixDataTable extends SimpleDataTable<RegionPublicIpPrefixResource> {
}

interface State extends DataBaseComponentState {
    redirectTo?: string;
    azureRegion: AzureRegionResource;
}

interface Props {
    azureRegion: AzureRegionResource;
    breadcrumbTitle: string;
}

export class AzureRegionDetail extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            azureRegion: props.azureRegion,
        };
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        const azureRegion = this.state.azureRegion;

        return <PaperLayout busy={this.state.busy}
                            errors={this.state.errors}
                            title="Detail"
                            breadcrumbTitle={this.props.breadcrumbTitle}
                            sectionControl={<ActionList actions={azureRegion ? this.getActions() : []}/>}>
            {azureRegion && this.getBody()}
        </PaperLayout>;
    }

    private getActions() {
        const menuItems = [
            OverflowMenu.dialogItem("Reprovision", this.renderReprovisionDialog()),
            OverflowMenu.dialogItem("Add IP prefix", <AddRegionPublicIpPrefixDialog azureRegion={this.state.azureRegion} onTaskStarted={this.redirectToTasks}/>),
            OverflowMenu.dialogItem("Republish IP ranges", this.renderRepublishStaticIpPrefixesDialog()),
        ];

        return [
            <OverflowMenu menuItems={menuItems}/>
        ];
    }

    private getBody() {
        const azureRegion = this.state.azureRegion;
        return azureRegion && <>
            <FormSectionHeading title="General"/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Azure Region</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{azureRegion.Region}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Resource Group</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {azureRegion.ResourceGroupName}&nbsp;(<ExternalLink href={externalSystemLinks.azurePortalLinks.resourceGroup(azureRegion.ResourceGroupName)}>Azure</ExternalLink>)
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Portal Instance</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{azureRegion.PortalInstanceName}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{azureRegion.Status}</DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Public IP Prefixes"/>
            <PublicIpPrefixDataTable
                data={azureRegion.PublicIpPrefixes}
                onRow={prefix => this.buildPublicIpPrefixRow(prefix)}
                headerColumns={["Name", "IP Prefix", "SKU", "Published", "Available"]}
                onRowOverflowMenu={prefix => this.getPublicIpPrefixOverflowMenu(prefix)}
                onEmpty={<div>This Azure Region doesn't have any IP prefixes yet.</div>}/>
        </>;
    }

    private buildPublicIpPrefixRow(prefix: RegionPublicIpPrefixResource) {
        return [
            prefix.Name,
            prefix.IpPrefix,
            prefix.Sku,
            prefix.IsPublished ? "Yes" : "No",
            prefix.IsAvailable ? "Yes" : "No",
        ];
    }

    private getPublicIpPrefixOverflowMenu(prefix: RegionPublicIpPrefixResource): MenuItem[] {
        const changeItems = getAllowedChanges(prefix).map(change =>
            OverflowMenu.dialogItem(changes[change].label, <ChangeRegionPublicIpPrefixDialog azureRegion={this.state.azureRegion}
                                                                                            prefix={prefix}
                                                                                            change={change}
                                                                                            onChange={this.updatePublicIpPrefix}/>));
        const deleteItems = canDelete(prefix)
            ? [OverflowMenu.dialogItem("Delete", <DeleteRegionPublicIpPrefixDialog azureRegion={this.state.azureRegion}
                                                                                prefix={prefix}
                                                                                onTaskStarted={this.redirectToTasks}/>)]
            : [];

        return [...changeItems, ...deleteItems];
    }

    private updatePublicIpPrefix = (updated: RegionPublicIpPrefixResource) => {
        this.setState(prevState => ({
            azureRegion: {
                ...prevState.azureRegion,
                PublicIpPrefixes: prevState.azureRegion.PublicIpPrefixes.map(p => p.Name === updated.Name ? updated : p),
            }
        }));
    }

    private redirectToTasks = () => {
        this.setState({redirectTo: azureRegionsRouteLinks.azureRegion(this.state.azureRegion.Id).tasks});
    }

    private republishStaticIpPrefixes = (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.AzureRegions.republishStaticIpPrefixes(this.state.azureRegion.Id);
            this.redirectToTasks();
        });
    }

    renderRepublishStaticIpPrefixesDialog() {
        return <SaveDialogLayout title="Republish IP Ranges"
                                 saveButtonLabel="Republish"
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={this.republishStaticIpPrefixes}>
            <AdministratorRoleCallout action="republish an Azure Region's IP ranges"/>
            <p>Send the currently published IP prefixes to Control Center for every instance in the {this.state.azureRegion.Region} Azure Region?</p>
            <p>This is what tells customers about IP prefixes that have been published or unpublished.</p>
        </SaveDialogLayout>;
    }

    private reprovision = (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.AzureRegions.reprovision(this.state.azureRegion);
            this.setState({redirectTo: azureRegionsRouteLinks.azureRegion(this.state.azureRegion.Id).tasks});

            return true;
        });
    }

    renderReprovisionDialog() {
        return <SaveDialogLayout title="Reprovision Azure Region"
                                 saveButtonLabel="Reprovision"
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={this.reprovision}>
                  <p>Reprovision the Azure Region?</p>
                </SaveDialogLayout>;
    }
}

export default AzureRegionDetail;
